// CETRA-38: keep legacy effect timing while clearing release ESLint warnings.
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
    Autocomplete,
    Box,
    Button,
    Dialog,
    FormControlLabel,
    IconButton,
    InputAdornment,
    Radio,
    RadioGroup,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import mixpanel from 'mixpanel-browser';
import StoreContext from '../../../react-core-lib/components/app/store-context';
import UserApi from '../../../rest-api/user-api';
import ClinikoApi from '../../../rest-api/cliniko-api';
import { getClinikoPatientIdFromStorage } from '../../helper/clinikoParamsHelper';
import { useGenerateLink } from './use-generated-link';
import { useSendEmail } from './use-send-email';
import ValidationUtils from '../../../react-core-lib/utils/validation-utils';
import BlockingSpinner from '../../UIControls/BlockingSpinner';
import ErrorDialog from '../../UIControls/api-button/ErrorDialog';
import {
    formatPatientAutocompleteLabel,
    formatPatientAutocompleteSearchText,
    patientAutocompleteNoOptionsText,
    patientAutocompleteSx,
    renderPatientAutocompleteOption,
} from '../../UIControls/patient-autocomplete-option';
import { PATIENT_EMAIL_LABEL, PATIENT_NAME_LABEL, PATIENT_SURNAME_LABEL } from '../../helper/patientLabels';
import { useSnackbar } from '../SnackbarProvider';
import {
    isAuthExpiredDialogVisible,
    isAuthExpiredError,
    notifyAuthExpired,
    subscribeAuthExpired,
} from '../../../utils/auth-error-utils';
import { designTokens } from '../../../theme';
import CloseIcon from '@mui/icons-material/Close';
import EmailIcon from '../../../assets/icons/Email.svg';
import WhatsappIcon from '../../../assets/icons/Whatsapp.svg';
import CopyIcon from '../../../assets/icons/Copy.svg';

const shareMethods = [
    { value: 'email', label: 'Email', icon: EmailIcon },
    { value: 'whatsapp', label: 'WhatsApp', icon: WhatsappIcon },
    { value: 'copy', label: 'Copy link', icon: CopyIcon },
];

const inputFieldSx = {
    '& .MuiOutlinedInput-root': {
        height: { xs: 'auto', sm: 52 },
        minHeight: { xs: '36px', sm: 'auto' },
        borderRadius: { xs: '8px', sm: '14px' },
        backgroundColor: '#FFFFFF',
        '& fieldset': {
            borderColor: '#E5E5E5',
            borderWidth: { xs: '0.6px', sm: '1.5px' },
        },
        '&:hover fieldset': {
            borderColor: '#E5E5E5',
        },
        '&.Mui-focused fieldset': {
            borderColor: '#FF669B',
        },
    },
};

const inputFieldPropsSx = {
    fontFamily: 'Inter, sans-serif',
    fontSize: { xs: '14px', sm: '16px' },
    letterSpacing: { xs: '-0.56px', sm: '-0.64px' },
    color: '#616063',
    borderRadius: { xs: '8px', sm: '14px' },
    height: { xs: 'auto', sm: '52px' },
    minHeight: { xs: '36px', sm: 'auto' },
    alignItems: 'center',
};

const actionButtonSx = {
    backgroundColor: designTokens.colors.secondaryButton,
    color: '#FFFFFF',
    borderRadius: { xs: '100px', sm: 36 },
    width: { xs: '100%', sm: '160px' },
    height: { xs: 'auto', sm: '44px' },
    padding: { xs: '14px 16px', sm: undefined },
    fontSize: { xs: 14, sm: 16 },
    fontWeight: 500,
    lineHeight: { xs: '14px', sm: '16px' },
    letterSpacing: { xs: '-0.28px', sm: '-0.64px' },
    textTransform: 'none',
    '&:hover': {
        backgroundColor: '#e65588',
    },
    '&.Mui-disabled': {
        backgroundColor: 'rgba(255, 102, 155, 0.5)',
        color: '#FFFFFF',
    },
};

const shareTabButtonSx = (selected) => ({
    borderRadius: '272px',
    padding: { xs: '14.5px', sm: '12px 20px' },
    minWidth: 'unset',
    whiteSpace: 'nowrap',
    backgroundColor: selected ? designTokens.colors.primaryButton : designTokens.colors.secondaryButton,
    color: '#FFFFFF',
    textTransform: 'none',
    fontSize: { xs: '14px', sm: '16px' },
    lineHeight: { xs: '14px', sm: '16px' },
    letterSpacing: { xs: '-0.56px', sm: '-0.32px' },
    fontWeight: 500,
    gap: { xs: 0, sm: '8px' },
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&:hover': {
        backgroundColor: selected ? '#0b0f6a' : '#e65588',
    },
});

const iconStyle = {
    width: 22,
    height: 22,
    display: 'block',
    filter: 'brightness(0) invert(1)',
};

const whatsappButtonSx = {
    backgroundColor: '#25D366',
    color: '#FFFFFF',
    borderRadius: { xs: '100px', sm: '30px' },
    width: { xs: '100%', sm: '240px' },
    height: { xs: 'auto', sm: '44px' },
    padding: { xs: '14px 16px', sm: undefined },
    fontSize: { xs: 14, sm: 16 },
    fontWeight: 500,
    lineHeight: { xs: '14px', sm: '16px' },
    letterSpacing: { xs: '-0.28px', sm: '-0.64px' },
    textTransform: 'none',
    '&:hover': {
        backgroundColor: '#1fb75a',
    },
};

const radioLabelSx = {
    '& .MuiFormControlLabel-label': {
        fontFamily: 'Inter, sans-serif',
        fontSize: { xs: 14, sm: 18 },
        lineHeight: { xs: '14px', sm: '18px' },
        letterSpacing: { xs: '-0.56px', sm: '-0.72px' },
        color: designTokens.colors.textSecondary,
    },
};

const formatClinikoPatientLabel = (patient) =>
    `${patient?.firstName || ''} ${patient?.lastName || ''}${patient?.email ? ` (${patient.email})` : ''}`.trim();

const formatAppointmentLabel = (appointment) => {
    const start = appointment?.starts_at || appointment?.appointment_start || appointment?.startsAt || '';
    const end = appointment?.ends_at || appointment?.appointment_end || appointment?.endsAt || '';
    if (!start || !end) return 'Unknown appointment';

    const startDate = new Date(start);
    const endDate = new Date(end);
    const dateLabel = startDate.toLocaleDateString('en-GB');
    const startTime = startDate.toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    });
    const endTime = endDate.toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    });
    return `${dateLabel}, ${startTime}-${endTime}`;
};

const getBundledHandoutNames = (handouts) =>
    Array.isArray(handouts) ? handouts.map((handout) => handout?.contentName).filter(Boolean) : [];

const getBundledEmailContentName = (handoutNames) =>
    handoutNames.length ? JSON.stringify(handoutNames) : 'your handouts';

const QuickShareDialog = ({ open, onClose, handout }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isShortViewport = useMediaQuery('(max-height:700px)');
    const useCompactDialog = isMobile || isShortViewport;
    const { authStore, userDataStore } = useContext(StoreContext);
    const showSnackbar = useSnackbar();
    const sendEmail = useSendEmail();
    const isBundledHandouts = Array.isArray(handout);
    const bundledHandoutNames = getBundledHandoutNames(handout);
    const shareTargetLabel = isBundledHandouts
        ? bundledHandoutNames.join(', ') || 'your handouts'
        : handout?.contentName || 'your handout';
    const emailContentName = isBundledHandouts ? getBundledEmailContentName(bundledHandoutNames) : shareTargetLabel;
    const { generateLink, generateLinkOnly, createShareData } = useGenerateLink(handout, isBundledHandouts);

    const [step, setStep] = useState('patient');
    const [inputMethod, setInputMethod] = useState('manual');
    const [manualPatientName, setManualPatientName] = useState('');
    const [manualPatientSurname, setManualPatientSurname] = useState('');
    const [selectedPatient, setSelectedPatient] = useState(null);
    const [patients, setPatients] = useState([]);
    const [isLoadingPatients, setIsLoadingPatients] = useState(false);
    const [clinikoPatient, setClinikoPatient] = useState(null);
    const [clinikoAppointments, setClinikoAppointments] = useState([]);
    const [selectedClinikoAppointment, setSelectedClinikoAppointment] = useState(null);
    const [clinikoLoading, setClinikoLoading] = useState(false);
    const [shareMethod, setShareMethod] = useState('email');
    const [email, setEmail] = useState('');
    const [generatedLink, setGeneratedLink] = useState('');
    const [integrationSource, setIntegrationSource] = useState(null);
    const [clinikoMeta, setClinikoMeta] = useState(null);
    const [isBlocking, setIsBlocking] = useState(false);
    const [errorDialogOpen, setErrorDialogOpen] = useState(false);
    const [errorDialogTitle, setErrorDialogTitle] = useState('Share error');
    const [errorDialogMessage, setErrorDialogMessage] = useState('There was an error sharing. Please try again.');

    const showClinikoOption = Boolean(clinikoPatient?.clinikoPatientId);

    const resetState = useCallback(() => {
        setStep('patient');
        setInputMethod('manual');
        setManualPatientName('');
        setManualPatientSurname('');
        setSelectedPatient(null);
        setPatients([]);
        setClinikoPatient(null);
        setClinikoAppointments([]);
        setSelectedClinikoAppointment(null);
        setShareMethod('email');
        setEmail('');
        setGeneratedLink('');
        setIntegrationSource(null);
        setClinikoMeta(null);
        setIsBlocking(false);
        setErrorDialogOpen(false);
    }, []);

    const closeDialog = useCallback(() => {
        resetState();
        onClose();
    }, [onClose, resetState]);

    const openAuthAwareErrorDialog = useCallback((error) => {
        if (isAuthExpiredError(error)) {
            notifyAuthExpired({ source: 'quick-share' });
            return;
        }
        setErrorDialogTitle('Share error');
        setErrorDialogMessage('There was an error sharing. Please try again.');
        setErrorDialogOpen(true);
    }, []);

    useEffect(() => {
        if (!open || !handout) {
            return undefined;
        }

        let isSubscribed = true;

        const fetchPatients = async () => {
            setIsLoadingPatients(true);
            try {
                const response = await UserApi.getPatientsByFilter(authStore.idToken, {
                    filterType: 'patientStatus',
                    filterValue: 'active',
                });

                if (!isSubscribed) return;

                const nextPatients = response?.patients || [];
                setPatients(nextPatients);
                if (nextPatients.length === 0) {
                    setInputMethod('manual');
                }
            } catch (error) {
                console.error('Failed to fetch patients:', error);
                if (isSubscribed) {
                    setPatients([]);
                    setInputMethod('manual');
                }
            } finally {
                if (isSubscribed) {
                    setIsLoadingPatients(false);
                }
            }
        };

        const fetchCliniko = async () => {
            const patientId = getClinikoPatientIdFromStorage();
            const hasClinikoKey = Boolean(userDataStore?.userData?.integrations?.cliniko?.apiKey);

            if (!patientId || !hasClinikoKey) {
                setClinikoPatient(null);
                setClinikoAppointments([]);
                return;
            }

            setClinikoLoading(true);
            try {
                const patient = await ClinikoApi.getPatientDetails(authStore.idToken, patientId);
                const appointmentsResponse = await ClinikoApi.getPatientAppointments(authStore.idToken, patientId);
                const appointments = Array.isArray(appointmentsResponse?.appointments)
                    ? appointmentsResponse.appointments
                    : [];

                if (!isSubscribed) return;

                setClinikoPatient(patient);
                setClinikoAppointments(appointments);
                if (patient?.clinikoPatientId) {
                    setInputMethod('cliniko');
                }
                if (appointments.length === 1) {
                    setSelectedClinikoAppointment(appointments[0]);
                }
            } catch (error) {
                console.warn('[Cliniko] Failed to prefill patient data:', error?.message || error);
                if (isSubscribed) {
                    setClinikoPatient(null);
                    setClinikoAppointments([]);
                }
            } finally {
                if (isSubscribed) {
                    setClinikoLoading(false);
                }
            }
        };

        fetchPatients();
        fetchCliniko();

        return () => {
            isSubscribed = false;
        };
    }, [open, handout, authStore.idToken, userDataStore?.userData?.integrations?.cliniko?.apiKey]);

    useEffect(() => {
        if (!open) {
            resetState();
        }
    }, [open, resetState]);

    useEffect(() => {
        if (!open) {
            return undefined;
        }

        if (isAuthExpiredDialogVisible()) {
            closeDialog();
            return undefined;
        }

        return subscribeAuthExpired(() => {
            closeDialog();
        });
    }, [open, closeDialog]);

    const isContinueDisabled = () => {
        if (inputMethod === 'manual') {
            return !manualPatientName && !manualPatientSurname;
        }
        if (inputMethod === 'select') {
            return !selectedPatient;
        }
        if (inputMethod === 'cliniko') {
            return !clinikoPatient || !selectedClinikoAppointment;
        }
        return true;
    };

    const buildPatientDetails = useCallback(() => {
        if (inputMethod === 'manual') {
            return {
                patientName: manualPatientName || null,
                patientSurname: manualPatientSurname || null,
            };
        }
        if (inputMethod === 'select' && selectedPatient) {
            return {
                patientName: selectedPatient.patientName || null,
                patientSurname: selectedPatient.patientSurname || null,
                patientId: selectedPatient.patientId || null,
                patientEmail: selectedPatient.patientEmail || null,
                clinicPatientId: selectedPatient.clinicPatientId || null,
            };
        }
        if (inputMethod === 'cliniko' && clinikoPatient) {
            return {
                patientName: clinikoPatient.firstName || null,
                patientSurname: clinikoPatient.lastName || null,
                patientEmail: clinikoPatient.email || null,
            };
        }
        return null;
    }, [clinikoPatient, inputMethod, manualPatientName, manualPatientSurname, selectedPatient]);

    const handleContinue = () => {
        if (inputMethod === 'cliniko') {
            setIntegrationSource('cliniko');
            setClinikoMeta({
                patientId: clinikoPatient?.clinikoPatientId || null,
                bookingId: selectedClinikoAppointment?.id || null,
            });
            setEmail(clinikoPatient?.email || '');
        } else {
            setIntegrationSource(null);
            setClinikoMeta(null);
            if (inputMethod === 'select') {
                setEmail(selectedPatient?.patientEmail || '');
            }
        }
        setStep('share');
        setGeneratedLink('');
        setShareMethod('email');
    };

    const getShareContext = useCallback(() => {
        if (integrationSource) {
            return { sharedViaIntegration: true, integrationSource };
        }
        return null;
    }, [integrationSource]);

    const addClinikoNote = async ({ linkId, clinicId }) => {
        if (!clinikoMeta?.patientId || !clinikoMeta?.bookingId) return;

        const templatesResponse = await ClinikoApi.getTreatmentNoteTemplates(authStore.idToken);
        const templateId = templatesResponse?.defaultTemplateId || null;
        if (!templateId) {
            console.warn('[Cliniko] No default treatment note template found.');
            return;
        }

        const url = new URL(process.env.REACT_APP_PATIENT_HANDOUT_URL);
        url.searchParams.append('linkId', linkId);
        url.searchParams.append('clinicId', clinicId);

        const now = new Date();
        const datePart = now.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
        });
        const timePart = now
            .toLocaleTimeString('en-GB', { hour: 'numeric', minute: '2-digit', hour12: true })
            .replace(' am', 'am')
            .replace(' pm', 'pm');
        const noteAnswer = `<p><b>Handout shared via Cetra Health</b></p><p>Handout: ${shareTargetLabel}</p><p>Link: <a href="${url.toString()}" target="_blank" rel="noopener noreferrer">View Handout</a></p><p>Date: ${datePart}, ${timePart}</p>`;

        await ClinikoApi.addNote(authStore.idToken, {
            patient_id: clinikoMeta.patientId,
            booking_id: clinikoMeta.bookingId,
            treatment_note_template_id: templateId,
            draft: false,
            content: {
                sections: [
                    {
                        questions: [
                            {
                                name: 'Patient progress report',
                                type: 'paragraph',
                                answer: '',
                            },
                            {
                                name: 'Assessment',
                                type: 'paragraph',
                                answer: '',
                            },
                            {
                                name: 'Treatment',
                                type: 'paragraph',
                                answer: noteAnswer,
                            },
                        ],
                    },
                ],
            },
        });
    };

    const handleSendEmail = async () => {
        if (!ValidationUtils.validateEmail(email)) {
            showSnackbar?.('Please enter a valid email address.', 'error');
            return;
        }

        if (isAuthExpiredDialogVisible()) {
            return;
        }

        setIsBlocking(true);
        try {
            const patientDetails = buildPatientDetails();
            const shareContext = getShareContext();
            const linkId = await generateLink(patientDetails, shareContext);
            const clinicId = userDataStore.userId;

            await sendEmail(email, emailContentName, linkId, clinicId, {});

            mixpanel.track('HandoutShared', {
                email: userDataStore?.userData?.emailAddress,
                shareMethod: 'Send Email',
                handoutTitle: shareTargetLabel,
            });

            try {
                await addClinikoNote({ linkId, clinicId });
            } catch (error) {
                console.warn('[Cliniko] Failed to add note:', error?.message || error);
            }

            showSnackbar?.('Email sent successfully.', 'success');
            closeDialog();
        } catch (error) {
            console.error('Quick share email error:', error);
            openAuthAwareErrorDialog(error);
        } finally {
            setIsBlocking(false);
        }
    };

    const handleShareWhatsapp = async () => {
        if (isAuthExpiredDialogVisible()) {
            return;
        }

        setIsBlocking(true);
        try {
            const patientDetails = buildPatientDetails();
            const shareContext = getShareContext();
            const { title, message } = await createShareData({}, patientDetails, shareContext);
            const isMobileShare = /Mobi|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
                navigator.userAgent,
            );

            if (isMobileShare && navigator.share) {
                await navigator.share({ title, text: message });
            } else {
                const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
                window.open(whatsappUrl, '_blank');
            }

            mixpanel.track('HandoutShared', {
                email: userDataStore?.userData?.emailAddress,
                shareMethod: isMobileShare ? 'Native Share' : 'WhatsApp',
                handoutTitle: shareTargetLabel,
            });

            closeDialog();
        } catch (error) {
            console.error('Quick share WhatsApp error:', error);
            openAuthAwareErrorDialog(error);
        } finally {
            setIsBlocking(false);
        }
    };

    const ensureCopyLink = useCallback(async () => {
        if (generatedLink) {
            return generatedLink;
        }
        const patientDetails = buildPatientDetails();
        const shareContext = getShareContext();
        return generateLinkOnly({}, patientDetails, shareContext);
    }, [buildPatientDetails, generatedLink, generateLinkOnly, getShareContext]);

    const handleCopyLink = async () => {
        if (isAuthExpiredDialogVisible()) {
            return;
        }

        setIsBlocking(true);
        let link = generatedLink;
        try {
            link = await ensureCopyLink();
            if (!generatedLink) {
                setGeneratedLink(link);
            }
            await navigator.clipboard.writeText(link);
            mixpanel.track('HandoutShared', {
                email: userDataStore?.userData?.emailAddress,
                shareMethod: 'Copy handout',
                handoutTitle: shareTargetLabel,
            });
            showSnackbar?.('Link copied to clipboard.', 'success');
            closeDialog();
        } catch (error) {
            console.warn('Clipboard API failed, using fallback method.', error);
            try {
                if (!link) {
                    link = await ensureCopyLink();
                }
                if (!generatedLink) {
                    setGeneratedLink(link);
                }
                const textArea = document.createElement('textarea');
                textArea.value = link;
                document.body.appendChild(textArea);
                textArea.focus();
                textArea.select();
                const successful = document.execCommand('copy');
                document.body.removeChild(textArea);
                if (!successful) {
                    throw new Error('Fallback copy command failed');
                }
                mixpanel.track('HandoutShared', {
                    email: userDataStore?.userData?.emailAddress,
                    shareMethod: 'Copy handout',
                    handoutTitle: shareTargetLabel,
                });
                showSnackbar?.('Link copied to clipboard.', 'success');
                closeDialog();
            } catch (fallbackError) {
                console.error('Fallback copy failed:', fallbackError);
                openAuthAwareErrorDialog(fallbackError);
            }
        } finally {
            setIsBlocking(false);
        }
    };

    useEffect(() => {
        if (step !== 'share' || shareMethod !== 'copy' || generatedLink) {
            return undefined;
        }

        let isMounted = true;
        const loadLink = async () => {
            try {
                setIsBlocking(true);
                const link = await ensureCopyLink();
                if (isMounted) {
                    setGeneratedLink(link);
                }
            } catch (error) {
                console.error('Failed to generate link:', error);
                openAuthAwareErrorDialog(error);
            } finally {
                if (isMounted) {
                    setIsBlocking(false);
                }
            }
        };

        loadLink();

        return () => {
            isMounted = false;
        };
    }, [step, shareMethod, generatedLink, ensureCopyLink, openAuthAwareErrorDialog]);

    const dialogHeight = useMemo(() => {
        if (shareMethod === 'whatsapp') return 357;
        return 419;
    }, [shareMethod]);
    const shouldUseAutoDialogHeight = useCompactDialog || step === 'patient';

    if (!handout) {
        return null;
    }

    return (
        <>
            <Dialog
                open={open}
                onClose={closeDialog}
                PaperProps={{
                    sx: {
                        borderRadius: '30px !important',
                        padding: useCompactDialog ? '30px 20px' : '30px 28px',
                        width: useCompactDialog ? 'calc(100% - 32px)' : '711px',
                        maxWidth: useCompactDialog ? 'calc(100% - 32px)' : '711px',
                        height: shouldUseAutoDialogHeight ? 'auto' : `${dialogHeight}px`,
                        maxHeight: 'calc(100dvh - 32px)',
                        boxSizing: 'border-box',
                        position: 'relative',
                        display: 'flex',
                        flexDirection: 'column',
                        overflowX: 'hidden',
                        overflowY: 'auto',
                    },
                }}
                maxWidth={false}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            mb: { xs: '8px', sm: '24px' },
                        }}
                    >
                        <Typography
                            sx={{
                                fontFamily: 'Inter, sans-serif',
                                fontWeight: 600,
                                fontSize: { xs: 26, sm: 32 },
                                lineHeight: { xs: '26px', sm: '32px' },
                                letterSpacing: { xs: '-1.04px', sm: '-1.28px' },
                                color: designTokens.colors.textPrimary,
                                pr: '44px',
                            }}
                        >
                            {step === 'patient'
                                ? 'Add patient details'
                                : isBundledHandouts
                                  ? 'Share handouts'
                                  : 'Share handout'}
                        </Typography>
                        <IconButton
                            onClick={closeDialog}
                            sx={{
                                p: 0,
                                width: { xs: '28px', sm: '36px' },
                                height: { xs: '28px', sm: '36px' },
                                background: 'rgb(246, 246, 246)',
                                borderRadius: { xs: '11.928px', sm: '50%' },
                                flexShrink: 0,
                            }}
                        >
                            <CloseIcon sx={{ width: { xs: '16px', sm: '24px' }, height: { xs: '16px', sm: '24px' } }} />
                        </IconButton>
                    </Box>

                    <Typography
                        sx={{
                            fontFamily: 'Inter, sans-serif',
                            fontSize: { xs: 14, sm: 18 },
                            lineHeight: { xs: '18px', sm: '18px' },
                            letterSpacing: { xs: '-0.56px', sm: '-0.72px' },
                            color: designTokens.colors.textSecondary,
                            mb: step === 'patient' ? { xs: '16px', sm: '30px' } : { xs: '12px', sm: '20px' },
                        }}
                    >
                        {step === 'patient'
                            ? 'Add patient details to share handout'
                            : `How would you like to share ${isBundledHandouts ? 'these handouts' : 'this handout'} with your patient?`}
                    </Typography>

                    {step === 'patient' && (
                        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                            <RadioGroup
                                value={inputMethod}
                                onChange={(event) => setInputMethod(event.target.value)}
                                sx={{ mb: { xs: '18px', sm: '28px' }, gap: { xs: 0.75, sm: 1 } }}
                            >
                                <FormControlLabel
                                    value="manual"
                                    control={<Radio sx={{ color: '#FF669B', '&.Mui-checked': { color: '#FF669B' } }} />}
                                    label="Enter patient details"
                                    sx={radioLabelSx}
                                />
                                <FormControlLabel
                                    value="select"
                                    control={<Radio sx={{ color: '#FF669B', '&.Mui-checked': { color: '#FF669B' } }} />}
                                    label="Select existing patient"
                                    disabled={isLoadingPatients || patients.length === 0}
                                    sx={radioLabelSx}
                                />
                                {showClinikoOption && (
                                    <FormControlLabel
                                        value="cliniko"
                                        control={
                                            <Radio sx={{ color: '#FF669B', '&.Mui-checked': { color: '#FF669B' } }} />
                                        }
                                        label="Choose a Cliniko patient"
                                        sx={radioLabelSx}
                                    />
                                )}
                            </RadioGroup>

                            {inputMethod === 'manual' && (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                                    <Box>
                                        <Typography
                                            sx={{
                                                fontFamily: 'Inter, sans-serif',
                                                fontSize: { xs: 14, sm: 18 },
                                                lineHeight: { xs: '14px', sm: '18px' },
                                                letterSpacing: { xs: '-0.56px', sm: '-0.72px' },
                                                color: designTokens.colors.textSecondary,
                                                mb: { xs: '8px', sm: '12px' },
                                            }}
                                        >
                                            {PATIENT_NAME_LABEL}
                                        </Typography>
                                        <TextField
                                            value={manualPatientName}
                                            onChange={(e) => setManualPatientName(e.target.value)}
                                            fullWidth
                                            sx={inputFieldSx}
                                            InputProps={{ sx: inputFieldPropsSx }}
                                            inputProps={{ className: 'mp-mask-patient' }}
                                        />
                                    </Box>
                                    <Box>
                                        <Typography
                                            sx={{
                                                fontFamily: 'Inter, sans-serif',
                                                fontSize: { xs: 14, sm: 18 },
                                                lineHeight: { xs: '14px', sm: '18px' },
                                                letterSpacing: { xs: '-0.56px', sm: '-0.72px' },
                                                color: designTokens.colors.textSecondary,
                                                mb: { xs: '8px', sm: '12px' },
                                            }}
                                        >
                                            {PATIENT_SURNAME_LABEL}
                                        </Typography>
                                        <TextField
                                            value={manualPatientSurname}
                                            onChange={(e) => setManualPatientSurname(e.target.value)}
                                            fullWidth
                                            sx={inputFieldSx}
                                            InputProps={{ sx: inputFieldPropsSx }}
                                            inputProps={{ className: 'mp-mask-patient' }}
                                        />
                                    </Box>
                                </Box>
                            )}

                            {inputMethod === 'select' && (
                                <Box sx={{ mb: { xs: 2, sm: 0 } }}>
                                    <Typography
                                        sx={{
                                            fontSize: { xs: 14, sm: 18 },
                                            lineHeight: { xs: '14px', sm: '18px' },
                                            letterSpacing: { xs: '-0.56px', sm: '-0.72px' },
                                            color: designTokens.colors.textSecondary,
                                            mb: { xs: '8px', sm: '12px' },
                                        }}
                                    >
                                        Select patient
                                    </Typography>
                                    <Autocomplete
                                        options={patients}
                                        getOptionLabel={formatPatientAutocompleteLabel}
                                        value={selectedPatient}
                                        onChange={(event, newValue) => setSelectedPatient(newValue)}
                                        noOptionsText={patientAutocompleteNoOptionsText}
                                        renderOption={renderPatientAutocompleteOption}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                sx={inputFieldSx}
                                                InputProps={{ ...params.InputProps, sx: inputFieldPropsSx }}
                                                inputProps={{ ...params.inputProps, className: 'mp-mask-patient' }}
                                            />
                                        )}
                                        filterOptions={(options, { inputValue }) => {
                                            const query = inputValue.trim().toLowerCase();
                                            if (!query) return options;
                                            return options.filter((patient) =>
                                                formatPatientAutocompleteSearchText(patient).includes(query),
                                            );
                                        }}
                                        fullWidth
                                        disabled={isLoadingPatients || patients.length === 0}
                                        sx={patientAutocompleteSx}
                                    />
                                </Box>
                            )}

                            {inputMethod === 'cliniko' && showClinikoOption && (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    <TextField
                                        label="Cliniko patient"
                                        value={formatClinikoPatientLabel(clinikoPatient)}
                                        fullWidth
                                        disabled
                                        sx={inputFieldSx}
                                        InputProps={{ sx: inputFieldPropsSx }}
                                        inputProps={{ className: 'mp-mask-patient' }}
                                    />
                                    <Autocomplete
                                        options={clinikoAppointments}
                                        getOptionLabel={formatAppointmentLabel}
                                        isOptionEqualToValue={(option, value) => option?.id === value?.id}
                                        value={selectedClinikoAppointment}
                                        onChange={(event, newValue) => setSelectedClinikoAppointment(newValue)}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Select appointment"
                                                sx={inputFieldSx}
                                                InputProps={{ ...params.InputProps, sx: inputFieldPropsSx }}
                                            />
                                        )}
                                        fullWidth
                                        disabled={clinikoLoading || clinikoAppointments.length === 0}
                                    />
                                </Box>
                            )}

                            {!isLoadingPatients && patients.length === 0 && !showClinikoOption && (
                                <Typography sx={{ fontSize: 12, color: designTokens.colors.textSecondary, mb: 2 }}>
                                    No patients found. Use manual entry to add patient details.
                                </Typography>
                            )}

                            {!clinikoLoading &&
                                inputMethod === 'cliniko' &&
                                showClinikoOption &&
                                clinikoAppointments.length === 0 && (
                                    <Typography sx={{ fontSize: 12, color: designTokens.colors.textSecondary, mb: 2 }}>
                                        No Cliniko appointments found. Choose a different option to continue.
                                    </Typography>
                                )}

                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: { xs: '24px', sm: '28px' } }}>
                                <Button sx={actionButtonSx} onClick={handleContinue} disabled={isContinueDisabled()}>
                                    Continue
                                </Button>
                            </Box>
                        </Box>
                    )}

                    {step === 'share' && (
                        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'flex-start',
                                    gap: { xs: '8px', sm: 2 },
                                    mb: { xs: '12px', sm: 2.5 },
                                    flexWrap: { xs: 'nowrap', sm: 'wrap' },
                                }}
                            >
                                {shareMethods.map((method) => (
                                    <Button
                                        key={method.value}
                                        sx={shareTabButtonSx(shareMethod === method.value)}
                                        onClick={() => setShareMethod(method.value)}
                                    >
                                        <img
                                            src={method.icon}
                                            alt={method.label}
                                            style={{
                                                ...iconStyle,
                                                width: isMobile ? 20 : 22,
                                                height: isMobile ? 20 : 22,
                                            }}
                                        />
                                        {!isMobile && method.label}
                                    </Button>
                                ))}
                            </Box>

                            {shareMethod === 'email' && (
                                <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                                    <Box>
                                        <Typography
                                            sx={{
                                                fontFamily: 'Inter, sans-serif',
                                                fontSize: { xs: 14, sm: 18 },
                                                lineHeight: { xs: '14px', sm: '18px' },
                                                letterSpacing: { xs: '-0.56px', sm: '-0.72px' },
                                                color: designTokens.colors.textSecondary,
                                                mb: { xs: '8px', sm: '12px' },
                                            }}
                                        >
                                            {PATIENT_EMAIL_LABEL}
                                        </Typography>
                                        <TextField
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            fullWidth
                                            sx={inputFieldSx}
                                            InputProps={{ sx: inputFieldPropsSx }}
                                            inputProps={{ className: 'mp-mask-patient' }}
                                        />
                                    </Box>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'center',
                                            mt: { xs: '24px', sm: 'auto' },
                                            pt: { sm: '24px' },
                                        }}
                                    >
                                        <Button sx={actionButtonSx} onClick={handleSendEmail}>
                                            {isMobile ? 'Send email' : 'Send'}
                                        </Button>
                                    </Box>
                                </Box>
                            )}

                            {shareMethod === 'whatsapp' && (
                                <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                                    <Typography
                                        sx={{
                                            fontFamily: 'Inter, sans-serif',
                                            fontSize: { xs: 14, sm: 18 },
                                            lineHeight: { xs: '18px', sm: '18px' },
                                            letterSpacing: { xs: '-0.56px', sm: '-0.72px' },
                                            color: designTokens.colors.textSecondary,
                                        }}
                                    >
                                        {'Use '}
                                        <Box component="span" sx={{ fontWeight: 500 }}>
                                            WhatsApp
                                        </Box>
                                        {' to send the handout below'}
                                    </Typography>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'center',
                                            mt: { xs: '24px', sm: 'auto' },
                                        }}
                                    >
                                        <Button sx={whatsappButtonSx} onClick={handleShareWhatsapp}>
                                            Send via WhatsApp
                                        </Button>
                                    </Box>
                                </Box>
                            )}

                            {shareMethod === 'copy' && (
                                <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                                    <Typography
                                        sx={{
                                            fontFamily: 'Inter, sans-serif',
                                            fontSize: { xs: 14, sm: 18 },
                                            lineHeight: { xs: '18px', sm: '18px' },
                                            letterSpacing: { xs: '-0.56px', sm: '-0.72px' },
                                            color: designTokens.colors.textSecondary,
                                            mb: { xs: '12px', sm: '20px' },
                                        }}
                                    >
                                        Copy the link below to share the handout via your chosen communication channel.
                                    </Typography>
                                    <TextField
                                        value={generatedLink}
                                        placeholder="Generating link..."
                                        fullWidth
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                height: { xs: '34px', sm: 52 },
                                                borderRadius: { xs: '8px', sm: '14px' },
                                                backgroundColor: '#FFFFFF',
                                                paddingRight: '0 !important',
                                                '& fieldset': {
                                                    borderColor: '#E5E5E5',
                                                    borderWidth: { xs: '0.6px', sm: '1.5px' },
                                                },
                                                '&:hover fieldset': { borderColor: '#E5E5E5' },
                                                '&.Mui-focused fieldset': { borderColor: '#FF669B' },
                                            },
                                        }}
                                        InputProps={{
                                            readOnly: true,
                                            endAdornment: (
                                                <InputAdornment
                                                    position="end"
                                                    sx={{
                                                        height: { xs: '34px', sm: '52px' },
                                                        maxHeight: 'none',
                                                        ml: 0,
                                                        mr: 0,
                                                    }}
                                                >
                                                    <IconButton
                                                        onClick={handleCopyLink}
                                                        disableRipple
                                                        sx={{
                                                            width: { xs: '40px', sm: '52px' },
                                                            height: { xs: '34px', sm: '52px' },
                                                            borderRadius: { xs: '0 8px 8px 0', sm: '0 14px 14px 0' },
                                                            backgroundColor: '#FF669B',
                                                            '&:hover': { backgroundColor: '#e65588' },
                                                        }}
                                                    >
                                                        <img
                                                            src={CopyIcon}
                                                            alt="Copy link"
                                                            style={{
                                                                width: isMobile ? 14 : 22,
                                                                height: isMobile ? 14 : 22,
                                                                filter: 'brightness(0) invert(1)',
                                                            }}
                                                        />
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                            sx: {
                                                ...inputFieldPropsSx,
                                                height: { xs: '34px', sm: '52px' },
                                                minHeight: 'unset',
                                                paddingRight: '0 !important',
                                            },
                                        }}
                                    />
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'center',
                                            mt: { xs: '24px', sm: 'auto' },
                                        }}
                                    >
                                        <Button sx={actionButtonSx} onClick={handleCopyLink}>
                                            Copy link
                                        </Button>
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    )}
                </Box>
            </Dialog>

            <BlockingSpinner open={open && (isBlocking || isLoadingPatients || clinikoLoading)} />
            <ErrorDialog
                open={errorDialogOpen}
                handleClose={() => setErrorDialogOpen(false)}
                title={errorDialogTitle}
                message={errorDialogMessage}
            />
        </>
    );
};

export default QuickShareDialog;
